import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Calendar as CalIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUJO_GLYPH = { event: '▲', task: '☐', note: '•' };

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysAway(target) {
  const today = startOfDay(new Date());
  const tgt = startOfDay(target);
  return Math.round((tgt - today) / 86400000);
}

function relLabel(target) {
  const n = daysAway(target);
  if (n < 0) return 'redan';
  if (n === 0) return 'idag';
  if (n === 1) return 'imorgon';
  if (n <= 6) return `om ${n} dagar`;
  if (n === 7) return 'om en vecka';
  return `om ${n} dagar`;
}

function urgencyStyle(target) {
  const n = daysAway(target);
  if (n <= 0) return { bg: 'from-[#FFE8E8] via-[#FFF0F6] to-[#FFE8F5]', border: '#D06892', accent: '#D06892' };
  if (n <= 1) return { bg: 'from-[#FFF8E1] via-[#FFF0F6] to-[#FFE8F5]', border: '#E0A330', accent: '#E0A330' };
  if (n <= 3) return { bg: 'from-[#FFF0F6] via-[#F5EEFF] to-[#E8F0FF]', border: '#8B63C4', accent: '#8B63C4' };
  return { bg: 'from-[#E8F0FF] via-[#F5EEFF] to-[#FFE8F5]', border: '#A2D2FF', accent: '#4A8BC7' };
}

const STORAGE_KEY = 'fc_dismissed_reminders_v1';

function loadDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    // Drop entries older than 24h
    const now = Date.now();
    const fresh = {};
    Object.entries(obj).forEach(([k, t]) => {
      if (typeof t === 'number' && now - t < 86400000) fresh[k] = t;
    });
    return fresh;
  } catch { return {}; }
}

function saveDismissed(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}


export default function ImportantReminders() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => loadDismissed());

  const load = useCallback(async () => {
    try {
      const today0 = startOfDay(new Date()).toISOString();
      const horizon = new Date(); horizon.setDate(horizon.getDate() + 7);
      const horizonIso = horizon.toISOString();
      const res = await axios.get(`${API}/calendar/events`, {
        params: { from_date: today0, to_date: horizonIso },
        withCredentials: true,
      });
      const list = (res.data?.items || [])
        .filter((e) => e.important && !e.completed)
        .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
      setItems(list);
    } catch {
      // silent — banner is non-critical
    }
  }, []);

  useEffect(() => {
    load();
    // refresh every 5 min in case user keeps app open
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = (key) => {
    const next = { ...dismissed, [key]: Date.now() };
    setDismissed(next);
    saveDismissed(next);
  };

  const visible = items
    .filter((e) => !dismissed[`${e.event_id}-${e.occurrence_date}`])
    .slice(0, 3);

  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="important-reminders">
      {visible.map((e) => {
        const dt = new Date(e.starts_at);
        const u = urgencyStyle(dt);
        const glyph = BUJO_GLYPH[e.bujo_kind || 'event'] || '▲';
        const key = `${e.event_id}-${e.occurrence_date}`;
        return (
          <div
            key={key}
            className={`neo-card px-3 py-2.5 bg-gradient-to-r ${u.bg} flex items-center gap-3`}
            style={{ borderLeftWidth: 6, borderLeftColor: u.accent }}
            data-testid={`reminder-${e.event_id}`}
          >
            <span className="text-2xl shrink-0" aria-hidden="true">🐹</span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
                <CalIcon className="w-3 h-3" />
                Viktig händelse
                <span className="opacity-50">·</span>
                <span className="capitalize">{relLabel(dt)}</span>
              </p>
              <p className="font-['Fredoka'] font-bold text-base truncate">
                <span className="text-[#D06892] mr-0.5">!</span>
                <span className="mr-1">{glyph}</span>
                {e.title}
                {!e.all_day && (
                  <span className="ml-2 text-xs opacity-70 font-normal">
                    {dt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => dismiss(key)}
              className="shrink-0 p-1.5 rounded-full hover:bg-white/60 transition-colors"
              data-testid={`reminder-dismiss-${e.event_id}`}
              aria-label="Dölj påminnelse"
              title="Dölj till imorgon"
            >
              <X className="w-4 h-4 opacity-60" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
